import type { TodayPriority } from '../api/client'

// A snoozed priority stays hidden for the rest of that day and comes back the
// next, if it is still true. Priorities are recomputed on every open and carry
// no identity on the server, so the snooze lives on this device only, keyed by
// the day it was made: yesterday's snoozes simply stop matching.

export type SnoozeStore = Pick<Storage, 'getItem' | 'setItem'>

const storageKey = 'harmovi.today.snoozed'

// A priority is identified by its type and client; the empty-slot one has no
// client, and one per day is all there is.
function keyOf(p: TodayPriority): string {
  return `${p.type}:${p.client_id ?? ''}`
}

function browserStore(): SnoozeStore | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function readSnoozed(date: string, store: SnoozeStore | null = browserStore()): Set<string> {
  try {
    const saved = JSON.parse(store?.getItem(storageKey) ?? 'null')
    if (saved?.date === date && Array.isArray(saved.keys)) {
      return new Set(saved.keys.filter((k: unknown) => typeof k === 'string'))
    }
  } catch {
    // Unreadable or blocked storage means nothing is snoozed.
  }
  return new Set()
}

// snooze returns the day's snoozed set with p added. It is saved when storage
// allows; when it doesn't, the returned set still hides p for this visit.
export function snooze(date: string, p: TodayPriority, store: SnoozeStore | null = browserStore()): Set<string> {
  const keys = readSnoozed(date, store)
  keys.add(keyOf(p))
  try {
    store?.setItem(storageKey, JSON.stringify({ date, keys: [...keys] }))
  } catch {
    // Not saved; it will be back on the next open.
  }
  return keys
}

export function unsnoozed(priorities: TodayPriority[], snoozed: Set<string>): TodayPriority[] {
  return priorities.filter((p) => !snoozed.has(keyOf(p)))
}
